
import React, { useState, useEffect } from "react";
import { CardContent } from "@/components/ui/card";
import { CampLocation, LocationVersion } from "@/types/campLocation";
import { UserInvitation } from "@/types/userInvitation";
import { useWelcomeLocationEdit } from "../hooks/useWelcomeLocationEdit";
import { Organization } from "@/types/organization";
import { getFirestore } from "firebase/firestore";
import { toast } from "sonner";
import { updateOrgDescription } from "@/services/organizationService";
import { useOrganizationData } from "@/hooks/useOrganizationData";
import { getCampOrganization } from "@/services/campOrganizationQuery";
import { createLogger } from "@/utils/logUtils";
import { WelcomeTabsManager } from "./tabs/WelcomeTabsManager";
import { publishLocationChanges } from "@/services/locationVersion/publishLocationService";
import { updateAllOffersForLocation } from "@/services/offer/offerBatchUpdateService";

const logger = createLogger("LocationEditingForm");

interface LocationEditingFormProps {
  location: CampLocation;
  invitation: UserInvitation;
  latestDraft?: LocationVersion | null;
  organizationData?: Organization | null;
  activeTab?: string;
  onTabChange?: (value: string) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  registerSaveHandler?: (fn: () => Promise<void>) => void;
}

const LocationEditingForm: React.FC<LocationEditingFormProps> = ({
  location,
  invitation,
  latestDraft,
  organizationData,
  activeTab: externalActiveTab,
  onTabChange: externalOnTabChange,
  onDirtyChange,
  registerSaveHandler
}) => {
  logger.info("Rendering with organizationData:", organizationData ? {
    id: organizationData.id,
    name: organizationData.name,
    descAboutTheOrg: organizationData.descAboutTheOrg ? 'present' : 'missing',
    descStaffingAndLeadership: organizationData.descStaffingAndLeadership ? 'present' : 'missing'
  } : 'missing');

  // Get the query client functions
  const {
    refreshOrganizationData
  } = useOrganizationData();

  // Local state for organization descriptions to handle them separately from the form
  const [aboutOrgText, setAboutOrgText] = useState(organizationData?.descAboutTheOrg || "");
  const [staffingText, setStaffingText] = useState(organizationData?.descStaffingAndLeadership || "");
  const [originalAboutOrgText, setOriginalAboutOrgText] = useState(organizationData?.descAboutTheOrg || "");
  const [originalStaffingText, setOriginalStaffingText] = useState(organizationData?.descStaffingAndLeadership || "");
  
  // State to persist the active tab across re-renders
  const [internalActiveTab, setInternalActiveTab] = useState("details");
  const activeTab = externalActiveTab ?? internalActiveTab;
  const setActiveTab = externalOnTabChange ?? setInternalActiveTab;
  const [isSavingOrgData, setIsSavingOrgData] = useState(false);

  // Track if org fields have changed from their original values
  const [orgFieldsChanged, setOrgFieldsChanged] = useState(false);
  // Ensure we don't mark dirty during initial hydration of org values
  const [orgValuesInitialized, setOrgValuesInitialized] = useState(false);

  // Get editing functionality
  const {
    form,
    isSaving,
    changeDescription,
    setChangeDescription,
    isDirty,
    handleManualSave,
    lastSavedTime,
    setIsDirty
  } = useWelcomeLocationEdit(location, invitation);

  // Update original values when organization data changes
  useEffect(() => {
    if (organizationData) {
      logger.info("Organization data updated, refreshing text fields", {
        descAboutTheOrg: organizationData.descAboutTheOrg?.substring(0, 20),
        descStaffingAndLeadership: organizationData.descStaffingAndLeadership?.substring(0, 20)
      });
      setOriginalAboutOrgText(organizationData.descAboutTheOrg || "");
      setOriginalStaffingText(organizationData.descStaffingAndLeadership || "");
      setAboutOrgText(organizationData.descAboutTheOrg || "");
      setStaffingText(organizationData.descStaffingAndLeadership || "");
      // Mark org values as initialized after syncing both originals and current values
      setOrgValuesInitialized(true);
    }
  }, [organizationData]);

  // Track changes in organization fields to mark form as dirty
  useEffect(() => {
    if (!organizationData) return;
    if (!orgValuesInitialized) return; // avoid false positives on initial load
    const hasOrgChanges = aboutOrgText !== originalAboutOrgText || staffingText !== originalStaffingText;
    logger.debug("Organization fields changed:", hasOrgChanges, {
      aboutOrgText: aboutOrgText?.substring(0, 20),
      originalAboutOrgText: originalAboutOrgText?.substring(0, 20),
      staffingText: staffingText?.substring(0, 20),
      originalStaffingText: originalStaffingText?.substring(0, 20)
    });
    setOrgFieldsChanged(hasOrgChanges);

    // Update the main form's dirty state to include org field changes
    if (hasOrgChanges) {
      setIsDirty(true);
    }
  }, [aboutOrgText, staffingText, originalAboutOrgText, originalStaffingText, organizationData, orgValuesInitialized, setIsDirty]);

  // Calculate version number from latest draft
  const versionNumber = latestDraft?.versionNumber || 1;

  // (moved effects to below where handleSaveWithOrgInfo is declared)

  // Function to fetch fresh organization data directly
  const fetchFreshOrganizationData = async (orgId: string) => {
    if (!orgId) return null;
    logger.info("Fetching fresh organization data for ID:", orgId);
    try {
      const db = getFirestore();
      const freshOrgData = await getCampOrganization(db, orgId);
      logger.info("Fresh organization data fetched:", {
        name: freshOrgData?.name,
        descAboutTheOrg: freshOrgData?.descAboutTheOrg ? 'present' : 'missing',
        descStaffingAndLeadership: freshOrgData?.descStaffingAndLeadership ? 'present' : 'missing'
      });
      return freshOrgData;
    } catch (error) {
      logger.error("Error fetching fresh organization data:", error);
      return null;
    }
  };

  // Handle saving the organization description fields when the location is saved
  const handleSaveWithOrgInfo = async () => {
    try {
      logger.info("Saving location and organization data");

      // Get userId from invitation
      const userId = invitation?.id || invitation?.token || "token-user";

      // STEP 1: First save the location data
      await handleManualSave(form);

      // Run publish + offers update in the background so UI stays responsive
      toast.info("Publishing in background… you can continue using the site.");
      (async () => {
        try {
          const db = getFirestore();

          // STEP 2: Publish changes (same as clicking “Publish changes”)
          const published = await publishLocationChanges(db, location.id, userId, {
            showToasts: false,
            refreshVersionHistory: () => {
              // Keep version history fresh in the UI
              window.dispatchEvent(new CustomEvent("refreshVersionHistory"));
            }
          });

          if (!published) {
            logger.error("Background publish failed or returned no version id");
            toast.error("Publish failed");
            return;
          }

          // STEP 3: Publish to offers (same as clicking “Publish to offers”)
          const res = await updateAllOffersForLocation(db, location.id, userId);
          if (res.success) {
            toast.success(`Published to offers (${res.updatedCount} sessions updated)`);
          } else {
            toast.error("Failed to publish to offers");
          }
        } catch (e) {
          logger.error("Background publish/update error:", e);
          toast.error("Background publish failed");
        }
      })();

      // Then save any org data if we have an organization (unchanged behavior)
      if (organizationData?.id && orgFieldsChanged) {
        logger.info("Saving organization fields:", {
          orgId: organizationData.id,
          aboutOrgTextLength: aboutOrgText?.length || 0,
          staffingTextLength: staffingText?.length || 0,
          aboutOrgTextSample: aboutOrgText?.substring(0, 30),
          staffingTextSample: staffingText?.substring(0, 30),
          userId
        });
        try {
          setIsSavingOrgData(true);
          const db = getFirestore();
          await updateOrgDescription(db, organizationData.id, aboutOrgText, staffingText, userId);

          // Invalidate the organizations query to force a refresh
          await refreshOrganizationData();

          // Also fetch the latest org data directly
          const freshOrgData = await fetchFreshOrganizationData(organizationData.id);
          if (freshOrgData) {
            // Manually update our local state with the fresh values
            logger.info("Updating local state with fresh organization data", {
              aboutOrgLength: freshOrgData.descAboutTheOrg?.length || 0,
              staffingLength: freshOrgData.descStaffingAndLeadership?.length || 0,
              aboutOrgSample: freshOrgData.descAboutTheOrg?.substring(0, 20),
              staffingSample: freshOrgData.descStaffingAndLeadership?.substring(0, 20)
            });

            // Update original values after successful save
            setOriginalAboutOrgText(freshOrgData.descAboutTheOrg || "");
            setOriginalStaffingText(freshOrgData.descStaffingAndLeadership || "");

            // Also update the current text fields
            setAboutOrgText(freshOrgData.descAboutTheOrg || "");
            setStaffingText(freshOrgData.descStaffingAndLeadership || "");
          } else {
            // If we couldn't fetch fresh data, at least update the original values
            logger.info("No fresh data fetched, using local values");
            setOriginalAboutOrgText(aboutOrgText);
            setOriginalStaffingText(staffingText);
          }

          // Reset the org fields changed flag since we've saved
          setOrgFieldsChanged(false);
          toast.success("Organization information saved");
        } catch (error) {
          logger.error("Error saving organization information:", error);
          toast.error("Failed to save organization information");
        } finally {
          setIsSavingOrgData(false);
        }
      } else {
        logger.info("No organization data to save or no changes detected", {
          orgId: organizationData?.id,
          orgFieldsChanged
        });
      }
    } catch (error) {
      logger.error("Error in handleSaveWithOrgInfo:", error);
      toast.error("Failed to save changes");
    }
  };

  // Notify parent about dirty state changes
  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isDirty || orgFieldsChanged);
    }
  }, [isDirty, orgFieldsChanged, onDirtyChange]);

  // Expose save handler to parent
  useEffect(() => {
    if (registerSaveHandler) {
      registerSaveHandler(handleSaveWithOrgInfo);
    }
  }, [registerSaveHandler, handleSaveWithOrgInfo]);

  return (
    <CardContent className="p-0">
      <WelcomeTabsManager
        location={location}
        invitation={invitation}
        latestDraft={latestDraft}
        organizationData={organizationData}
        form={form}
        isSaving={isSaving}
        isSavingOrgData={isSavingOrgData}
        isDirty={isDirty}
        orgFieldsChanged={orgFieldsChanged}
        changeDescription={changeDescription}
        setChangeDescription={setChangeDescription}
        lastSavedTime={lastSavedTime}
        handleSaveWithOrgInfo={handleSaveWithOrgInfo}
        aboutOrgText={aboutOrgText}
        setAboutOrgText={setAboutOrgText}
        staffingText={staffingText}
        setStaffingText={setStaffingText}
        versionNumber={versionNumber}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </CardContent>
  );
};
export default LocationEditingForm;
